import { FC, useEffect } from "react";

import { StopRecordingMenuItem } from "components/menus/RecordingMenuItems";
import { RecordingSessionState } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu } from "mds/components/Menu";
import { Tooltip } from "mds/components/Tooltip";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ChevronDownIcon, RecordingIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectActiveRecordingSessionForCurrentSection } from "store/selectors";
import { toastWarningMessage } from "utils/alerts";
import { useAv } from "utils/av";

type RecordingStatusIndicatorProps = {
  className?: string;
  isInMobileMenu?: boolean;
};

// Persists across remounts (e.g. on window resize) to avoid duplicate toasts
let lastActiveRecordingSessionId: string | undefined;

export const RecordingStatusIndicator: FC<RecordingStatusIndicatorProps> = ({
  className,
  isInMobileMenu,
}) => {
  const { canParticipateInSection, canStartAndStopRecording } = useCapabilities();
  const { isInVirtualClass } = useAv();

  const activeRecordingSession = useAppSelector(selectActiveRecordingSessionForCurrentSection);
  const isRecordingSessionStarting =
    activeRecordingSession?.state === RecordingSessionState.STARTING;
  const isRecordingSessionStarted = activeRecordingSession?.state === RecordingSessionState.STARTED;
  const isRecordingSessionStopped = !activeRecordingSession;

  useEffect(() => {
    // We don't want to show a toast every time the mobile menu is open.
    if (isInMobileMenu) {
      return;
    }

    if (canParticipateInSection && isInVirtualClass) {
      const sessionId = activeRecordingSession?.id;

      if (isRecordingSessionStarted && sessionId && sessionId !== lastActiveRecordingSessionId) {
        toastWarningMessage(t("recording_controls.toast_recording_active"));
        lastActiveRecordingSessionId = sessionId;
      } else if (lastActiveRecordingSessionId && isRecordingSessionStopped) {
        toastWarningMessage(t("recording_controls.toast_recording_stopped"));
        lastActiveRecordingSessionId = undefined;
      }
    }
  }, [
    activeRecordingSession?.id,
    canParticipateInSection,
    isInVirtualClass,
    isRecordingSessionStarted,
    isRecordingSessionStopped,
    isInMobileMenu,
  ]);

  if (!isInVirtualClass) {
    return null;
  }

  if (
    !isInMobileMenu &&
    canStartAndStopRecording &&
    (isRecordingSessionStarting || isRecordingSessionStarted)
  ) {
    const recordingStatus = isRecordingSessionStarting
      ? t("recording_controls.menu_title_starting")
      : t("recording_controls.menu_title_started");
    const tooltipText = isRecordingSessionStarting
      ? t("recording_controls.menu_title_starting_tooltip")
      : t("recording_controls.menu_title_started_tooltip");

    const MenuButton = (
      <div className={className}>
        <Tooltip content={tooltipText} place="left">
          <SquareButton aria-label={tooltipText} kind="tertiary" size="s">
            <IconText
              className="text-orange"
              iconEnd={<ChevronDownIcon />}
              iconStart={<RecordingIcon />}
              text={recordingStatus}
              hideTextOnSm
            />
          </SquareButton>
        </Tooltip>
      </div>
    );

    return (
      <Menu menuButton={MenuButton}>
        <StopRecordingMenuItem recordingSessionId={activeRecordingSession.id} />
      </Menu>
    );
  }

  if ((isInMobileMenu || canParticipateInSection) && isRecordingSessionStarted) {
    const recordingStatus = t("recording_controls.menu_title_started");
    const recordingStatusTooltip = t("recording_controls.menu_title_started_tooltip");

    return (
      <div className={className}>
        <TooltipTrigger content={isInMobileMenu ? "" : recordingStatusTooltip}>
          <IconText
            className="text-orange"
            hideTextOnSm={!isInMobileMenu}
            iconStart={<RecordingIcon />}
            text={isInMobileMenu ? recordingStatusTooltip : recordingStatus}
          />
        </TooltipTrigger>
      </div>
    );
  }

  return null;
};
